<script>
  import {onMount} from 'svelte';
  import {fade} from 'svelte/transition';
  import Metadata from '$features/seo/Metadata.svelte';
  import {Button} from '$components/jera';

  let mounted = $state(false);
  let visibleSections = $state(new Set());

  onMount(() => {
    mounted = true;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-animate');
            if (id && !visibleSections.has(id)) {
              visibleSections = new Set([...visibleSections, id]);
            }
          }
        });
      },
      {threshold: 0.15}
    );

    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  });

  const subscriptionTiers = [
    {
      name: 'Free',
      price: '$0',
      cadence: 'per month',
      tagline: 'Perfect for testing J\'ko risk-free',
      description: 'Test the platform with real customers before committing. No credit card required. Perfect for boutiques exploring virtual try-ons.',
      features: [
        '125,000 weekly credits (refreshes every Monday)',
        'Unlimited SKUs (no per-garment fees)',
        'Instagram DM automation included',
        'AI virtual try-on technology',
        'Shopify integration with attribution',
        'Community support',
        'Basic analytics dashboard'
      ],
      cta: 'Start free - no credit card',
      href: 'https://dash.selify.ai',
      variant: 'secondary'
    },
    {
      name: 'Starter',
      price: '$49',
      cadence: 'per month',
      tagline: 'For brands doing $100K-500K/year',
      description: 'Designed for brands ready to reduce returns and automate Instagram sales. Everything you need to launch and scale.',
      features: [
        '1,470,000 weekly credits (10× more than Free)',
        'Everything in Free, plus:',
        'Priority email support (24-hour response)',
        'Advanced analytics with export',
        'API access for automation',
        'Custom branding options',
        'Product photoshoot generation',
        'Multi-platform support (Instagram, WhatsApp, Messenger)',
        'Revenue attribution dashboard',
        'Multilingual chatbot'
      ],
      cta: 'Get Started',
      href: 'https://dash.selify.ai',
      variant: 'accent',
      featured: true,
      badge: 'Most Popular'
    },
    {
      name: 'Pro',
      price: '$249',
      cadence: 'per month',
      tagline: 'For brands doing $500K-5M/year',
      description: 'For scaling brands serious about cutting return costs and maximizing Instagram ROI. Premium features and dedicated support.',
      features: [
        '8,715,000 weekly credits (5× more than Starter)',
        'Everything in Starter, plus:',
        'Priority processing queue (<10 sec avg)',
        'Dedicated account manager',
        'Advanced Shopify integrations',
        'Bulk product operations',
        'Custom workflow automation',
        'White-label options',
        'Try-on → Purchase attribution per product',
        'A/B testing for chatbot responses',
        'Advanced funnel analytics'
      ],
      cta: 'Scale with Pro',
      href: 'https://dash.selify.ai',
      variant: 'primary'
    },
    {
      name: 'Enterprise',
      price: '$999',
      cadence: 'per month',
      tagline: 'For brands doing $5M+/year',
      description: 'For high-volume brands that need white-glove support, custom SLAs, and unlimited scale. Premium enterprise features.',
      features: [
        '39,960,000 weekly credits (20× more than Starter)',
        'Everything in Pro, plus:',
        'Highest priority processing',
        'Dedicated success team',
        'Custom SLA guarantees (99.9% uptime)',
        'Advanced security features',
        'Custom contracts available',
        'Invoice billing',
        'Volume discounts',
        'Custom domain for try-on results',
        'Quarterly business reviews',
        'Direct Slack/phone support'
      ],
      cta: 'Contact Sales',
      href: 'mailto:hello@selify.ai',
      variant: 'accent'
    }
  ];

  const creditPackages = [
    {
      name: 'Starter',
      credits: 50,
      price: 20,
      savings: null,
      useCase: 'Top up a capsule drop or weekend activation',
      variant: 'secondary'
    },
    {
      name: 'Standard',
      credits: 100,
      price: 35,
      savings: '13% off',
      useCase: 'Maintain steady campaign momentum',
      variant: 'secondary'
    },
    {
      name: 'Growth',
      credits: 200,
      price: 60,
      savings: '25% off',
      useCase: 'Most loved by scaling brands',
      variant: 'primary',
      badge: 'Most Popular'
    },
    {
      name: 'Pro',
      credits: 500,
      price: 125,
      savings: '38% off',
      useCase: 'Support high-volume collections',
      variant: 'accent'
    },
    {
      name: 'Enterprise',
      credits: 1000,
      price: 200,
      savings: '50% off',
      useCase: 'Agencies and multi-brand portfolios',
      variant: 'secondary',
      badge: 'Best Value'
    }
  ];

  const formatCurrency = (value) => `$${value}`;
  const formatQuantity = (credits) => `${credits} credits`;
  const formatRate = (price, credits) => `$${(price / credits).toFixed(2)} / credit`;

  const principles = [
    {
      title: 'No Per-Garment Fees',
      description:
        'Unlike PICTOFiT ($5/garment), J\'ko includes unlimited SKUs in all plans. Upload your entire catalog without worrying about extra charges. Your subscription covers everything.'
    },
    {
      title: 'End-to-End Attribution',
      description:
        'Track every Instagram post → DM → Try-on → Purchase. Know exactly which products drive sales and which campaigns deliver ROI. This level of attribution is exclusive to J\'ko.'
    },
    {
      title: 'ROI-Focused Pricing',
      description:
        'Most brands save $50-100K/year in reduced return costs alone. J\'ko isn\'t an expense - it\'s an investment that pays for itself many times over. Our pricing reflects the value we deliver.'
    }
  ];

  const faqs = [
    {
      question: 'How much will I save on returns?',
      answer:
        'Industry data shows virtual try-on reduces fit-related returns by 30-40%. For a $1M/year brand with a 30% return rate, that\'s $50-100K/year in saved return processing costs. Your ROI depends on your current return rate and revenue, but most brands see 15-50× return on their J\'ko investment.'
    },
    {
      question: 'How do credits work?',
      answer:
        'Credits are the universal unit for all J\'ko actions. Virtual try-ons, bot messages, and product photoshoots each consume credits. Your subscription includes a generous weekly credit allowance that refreshes every Monday (use it or lose it). If you need more, you can add pay-as-you-go credits that never expire.'
    },
    {
      question: 'What happens when credits run out?',
      answer:
        'You\'ll receive warnings at 20% and 10% remaining. If you hit 0%, you can instantly add pay-as-you-go credits or upgrade your subscription. Most brands choose a plan with headroom to avoid interruptions. No surprise charges - the service pauses until you add credits.'
    },
    {
      question: 'Why is J\'ko better than ManyChat + a try-on app?',
      answer:
        'Three reasons: (1) Attribution - Know which Instagram post led to which sale. Competitors can\'t track this. (2) Integration - One dashboard, one login, unified data. No fragmentation. (3) Cost - ManyChat ($50/mo) + Antla ($130/mo) = $180/mo with NO attribution. J\'ko Pro = $249/mo with everything integrated.'
    },
    {
      question: 'Do you charge per garment like PICTOFiT?',
      answer:
        'Absolutely not. Upload unlimited SKUs at no extra cost. PICTOFiT charges $5 per garment, which is $500/mo for 100 products! J\'ko includes unlimited products in all plans, even Free.'
    },
    {
      question: 'What revenue should my brand be at to justify J\'ko?',
      answer:
        'Free: Testing or <$50K/year. Starter ($49/mo): $100K-500K/year. Pro ($249/mo): $500K-5M/year. Enterprise ($999/mo): $5M+/year. If you\'re doing $100K+/year with a 30% return rate, the ROI justifies Starter immediately (you\'ll save $15-30K/year in returns alone).'
    }
  ];

  const getDelay = (index, base = 0) => base + index * 0.12;
</script>

<Metadata
  title="Pricing - Selify AI | Save $50-100K/Year on Returns"
  description="Simple, transparent pricing for fashion brands. Free tier available. Subscriptions from $49/mo. Unlimited SKUs. No per-garment fees. Reduce returns 30-40%. ROI: 15-50×."
/>

<main class="pricing-page">
  <section class="hero" class:mounted>
    <div class="hero-content">
      <span class="eyebrow">Simple, Transparent Pricing</span>
      <h1>Pricing That Scales With Your Business</h1>
      <p class="hero-description">
        Start free. Upgrade as you grow. All plans include unlimited SKUs, Shopify integration, and Instagram automation. No per-garment fees. No hidden costs.
      </p>
      <div class="hero-actions">
        <Button variant="primary" size="lg" href="https://dash.selify.ai">Launch dashboard</Button>
        <Button variant="accent" size="lg" href="mailto:hello@selify.ai">Consult our team</Button>
      </div>
      <p class="hero-note">
        All plans include generous weekly credit allowances that refresh every Monday. Credits cover virtual try-ons, bot messages, and product photoshoots. Need more? Add pay-as-you-go credits anytime.
      </p>
    </div>
  </section>

  <section class="tiers-section" data-animate="tiers" class:visible={visibleSections.has('tiers')}>
    <div class="section-header">
      <span class="section-eyebrow">Choose Your Plan</span>
      <h2>Start free, scale as you grow</h2>
      <p class="section-description">
        All plans include unlimited SKUs, Shopify integration, and Instagram automation. Weekly credits refresh every Monday. Choose the tier that matches your revenue and growth goals.
      </p>
    </div>

    <div class="tiers-grid">
      {#each subscriptionTiers as tier, i}
        <article
          class="tier-card"
          class:featured={tier.featured}
          data-animate={`tier-${i}`}
          class:visible={visibleSections.has(`tier-${i}`)}
          style={`--delay: ${getDelay(i)}s`}
          in:fade={{duration: 400, delay: i * 100}}
        >
          {#if tier.badge}
            <div class="card-badge">{tier.badge}</div>
          {/if}
          <div class="tier-head">
            <span class="tier-name">{tier.name}</span>
            <div class="tier-price">
              <span class="price-amount">{tier.price}</span>
              {#if tier.cadence}
                <span class="price-cadence">{tier.cadence}</span>
              {/if}
            </div>
            <p class="tier-tagline">{tier.tagline}</p>
            <p class="tier-description">{tier.description}</p>
          </div>

          <ul class="tier-features">
            {#each tier.features as feature}
              <li>{feature}</li>
            {/each}
          </ul>

          <div class="tier-footer">
            <Button fullWidth variant={tier.variant} href={tier.href}>
              {tier.cta}
            </Button>
          </div>
        </article>
      {/each}
    </div>
  </section>

  <section class="credits-section" data-animate="credits" class:visible={visibleSections.has('credits')}>
    <div class="section-header">
      <span class="section-eyebrow">Pay-As-You-Go Credits</span>
      <h2>Need more credits? Add them anytime</h2>
      <p class="section-description">
        Perfect for seasonal campaigns or usage spikes. PAYG credits never expire, so you can bank them ahead of product launches. Subscriptions offer better value for consistent usage.
      </p>
    </div>

    <div class="credits-grid">
      {#each creditPackages as pack, i}
        <article
          class="credit-card"
          class:featured={pack.variant === 'primary'}
          data-animate={`credits-${i}`}
          class:visible={visibleSections.has(`credits-${i}`)}
          style={`--delay: ${getDelay(i, 0.1)}s`}
          in:fade={{duration: 400, delay: i * 80}}
        >
          {#if pack.badge}
            <div class="card-badge">{pack.badge}</div>
          {/if}
          <div class="credit-head">
            <span class="credit-name">{pack.name}</span>
            <span class="credit-quantity">{formatQuantity(pack.credits)}</span>
          </div>
          <div class="credit-pricing">
            <span class="credit-price">{formatCurrency(pack.price)}</span>
            <span class="credit-rate">{formatRate(pack.price, pack.credits)}</span>
          </div>
          <p class="credit-savings">{pack.useCase}</p>
          {#if pack.savings}
            <p class="credit-savings-highlight">{pack.savings}</p>
          {/if}
          <Button fullWidth variant={pack.variant} href="https://dash.selify.ai">
            Purchase credits
          </Button>
        </article>
      {/each}
    </div>

    <div class="credit-footnote">
      <span>Pay-as-you-go credits never expire. Perfect for seasonal campaigns or usage spikes. Subscriptions offer better value for consistent usage.</span>
    </div>
  </section>

  <section class="principles-section" data-animate="principles" class:visible={visibleSections.has('principles')}>
    <div class="principles-grid">
      {#each principles as principle, i}
        <article
          class="principle-card"
          data-animate={`principle-${i}`}
          class:visible={visibleSections.has(`principle-${i}`)}
          style={`--delay: ${getDelay(i, 0.15)}s`}
          in:fade={{duration: 400, delay: i * 80}}
        >
          <h3>{principle.title}</h3>
          <p>{principle.description}</p>
        </article>
      {/each}
    </div>
  </section>

  <section class="faq-section" data-animate="faq" class:visible={visibleSections.has('faq')}>
    <div class="section-header">
      <span class="section-eyebrow">Frequently Asked Questions</span>
      <h2>Everything you need to know</h2>
    </div>

    <div class="faq-grid">
      {#each faqs as faq, i}
        <article
          class="faq-item"
          data-animate={`faq-${i}`}
          class:visible={visibleSections.has(`faq-${i}`)}
          style={`--delay: ${getDelay(i, 0.2)}s`}
        >
          <h3>{faq.question}</h3>
          <p>{faq.answer}</p>
        </article>
      {/each}
    </div>
  </section>

  <section class="cta-section" data-animate="cta" class:visible={visibleSections.has('cta')}>
    <div class="cta-content">
      <h2>Ready to reduce returns and increase revenue?</h2>
      <p>
        Join fashion brands using Selify to cut return costs by 30-40% and track Instagram → Purchase attribution. Start free, no credit card required. Launch in 30 minutes.
      </p>
      <div class="cta-actions">
        <Button variant="primary" href="https://dash.selify.ai">Create an account</Button>
        <Button variant="secondary" href="mailto:hello@selify.ai">Book a white-glove demo</Button>
      </div>
    </div>
  </section>
</main>

<style lang="postcss">
  @reference '$theme';

  .pricing-page {
    @apply min-h-screen bg-base0 text-base6;
  }

  .hero {
    @apply relative overflow-hidden px-6 py-24 text-center;
    background: linear-gradient(160deg, rgba(var(--color-base1-rgb), 0.9) 0%, rgba(var(--color-base0-rgb), 1) 55%, rgba(var(--color-base14-rgb), 0.08) 100%);
  }

  .hero::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 20%, rgba(var(--color-base14-rgb), 0.18), transparent 45%), radial-gradient(circle at 80% 35%, rgba(var(--color-base3-rgb), 0.16), transparent 50%);
    opacity: 0;
    transition: opacity 1s ease;
  }

  .hero.mounted::after {
    opacity: 1;
  }

  .hero-content {
    @apply relative mx-auto flex max-w-4xl flex-col items-center gap-6;
    opacity: 0;
    transform: translateY(24px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .hero.mounted .hero-content {
    opacity: 1;
    transform: translateY(0);
  }

  .eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.35em] text-base4;
    letter-spacing: 0.3em;
  }

  h1 {
    @apply text-4xl font-semibold text-base6 md:text-5xl lg:text-6xl;
    letter-spacing: -0.01em;
  }

  .hero-description {
    @apply max-w-2xl text-lg text-base4;
  }

  .hero-actions {
    @apply mt-4 flex flex-wrap items-center justify-center gap-4;
  }

  .hero-note {
    @apply max-w-2xl text-sm text-base4/80;
  }

  .section-header {
    @apply mx-auto mb-12 max-w-3xl text-center;
  }

  .section-eyebrow {
    @apply text-xs font-semibold uppercase tracking-[0.35em] text-base4;
  }

  .section-description {
    @apply mt-4 text-base text-base4;
  }

  .tiers-section {
    @apply mx-auto max-w-7xl px-6 py-20;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .tiers-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .tiers-grid {
    @apply grid gap-6 md:grid-cols-2 lg:grid-cols-3;
  }

  .tier-card {
    @apply relative flex h-full flex-col gap-6 rounded-3xl border border-base3/20 bg-base1/60 p-8;
    backdrop-filter: blur(22px) saturate(180%);
    -webkit-backdrop-filter: blur(22px) saturate(180%);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.12);
    opacity: 0;
    transform: translateY(24px);
    transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .tier-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .tier-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 24px 72px rgba(0, 0, 0, 0.18);
  }

  .tier-card.featured {
    border-color: var(--color-base14);
    background: linear-gradient(135deg, rgba(var(--color-base14-rgb), 0.12), rgba(var(--color-base1-rgb), 0.65));
    box-shadow: 0 24px 80px rgba(var(--color-base14-rgb), 0.24);
  }

  .card-badge {
    @apply absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-base14 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-white;
  }

  .tier-head {
    @apply flex flex-col gap-4 border-b border-base3/30 pb-6;
  }

  .tier-name {
    @apply text-sm font-semibold uppercase tracking-[0.3em] text-base4;
  }

  .tier-price {
    @apply flex items-baseline gap-2;
  }

  .price-amount {
    @apply text-4xl font-bold text-base6;
  }

  .price-cadence {
    @apply text-sm uppercase tracking-[0.2em] text-base4;
  }

  .tier-tagline {
    @apply text-base text-base5;
  }

  .tier-description {
    @apply text-sm text-base4;
  }

  .tier-features {
    @apply flex flex-1 flex-col gap-3;
  }

  .tier-features li {
    @apply relative pl-6 text-sm text-base5;
  }

  .tier-features li::before {
    content: '';
    @apply absolute left-0 top-1.5 h-3 w-3 rounded-full bg-base14/80;
    box-shadow: 0 0 12px rgba(var(--color-base14-rgb), 0.45);
  }

  .tier-footer {
    @apply pt-2;
  }

  .credits-section {
    @apply mx-auto max-w-6xl px-6 py-20;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .credits-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .credits-grid {
    @apply grid gap-6 md:grid-cols-2 lg:grid-cols-3;
  }

  .credit-card {
    @apply relative flex h-full flex-col gap-4 rounded-3xl border border-base3/20 bg-base1/50 p-6;
    backdrop-filter: blur(18px) saturate(160%);
    -webkit-backdrop-filter: blur(18px) saturate(160%);
    box-shadow: 0 16px 42px rgba(0, 0, 0, 0.12);
    opacity: 0;
    transform: translateY(24px);
    transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .credit-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .credit-card.featured {
    border-color: var(--color-base14);
    background: linear-gradient(140deg, rgba(var(--color-base14-rgb), 0.16), rgba(var(--color-base1-rgb), 0.55));
  }

  .credit-head {
    @apply flex items-center justify-between;
  }

  .credit-name {
    @apply text-sm font-semibold uppercase tracking-[0.3em] text-base4;
  }

  .credit-quantity {
    @apply text-base text-base6;
  }

  .credit-pricing {
    @apply flex items-baseline gap-3;
  }

  .credit-price {
    @apply text-3xl font-semibold text-base6;
  }

  .credit-rate {
    @apply text-sm text-base4;
  }

  .credit-savings {
    @apply text-sm text-base5;
  }

  .credit-savings-highlight {
    @apply text-xs font-semibold uppercase tracking-[0.25em] text-base14;
  }

  .credit-footnote {
    @apply mt-10 text-center text-sm text-base4;
  }

  .principles-section {
    @apply mx-auto max-w-5xl px-6 py-20;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .principles-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .principles-grid {
    @apply grid gap-6 md:grid-cols-3;
  }

  .principle-card {
    @apply rounded-3xl border border-base3/20 bg-base1/40 p-8 text-left;
    backdrop-filter: blur(14px) saturate(150%);
    -webkit-backdrop-filter: blur(14px) saturate(150%);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .principle-card.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .principle-card h3 {
    @apply mb-3 text-lg font-semibold text-base6;
  }

  .principle-card p {
    @apply text-sm text-base4;
  }

  .faq-section {
    @apply mx-auto max-w-5xl px-6 py-20;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .faq-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .faq-grid {
    @apply grid gap-6 md:grid-cols-2;
  }

  .faq-item {
    @apply rounded-3xl border border-base3/25 bg-base1/50 p-6;
    backdrop-filter: blur(14px) saturate(160%);
    -webkit-backdrop-filter: blur(14px) saturate(160%);
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
    transition-delay: var(--delay, 0s);
  }

  .faq-item.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .faq-item h3 {
    @apply mb-3 text-base font-semibold text-base6;
  }

  .faq-item p {
    @apply text-sm text-base4;
  }

  .cta-section {
    @apply mx-auto max-w-5xl px-6 pb-24;
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .cta-section.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .cta-content {
    @apply flex flex-col items-center gap-6 rounded-[36px] border border-base3/20 bg-gradient-to-br from-base2 via-base1 to-base14/10 px-12 py-20 text-center shadow-xl;
  }

  .cta-content h2 {
    @apply text-3xl font-semibold text-base6 md:text-4xl;
  }

  .cta-content p {
    @apply max-w-2xl text-lg text-base4;
  }

  .cta-actions {
    @apply mt-4 flex flex-wrap justify-center gap-4;
  }

  @media (max-width: 1024px) {
    .tiers-grid {
      @apply md:grid-cols-2;
    }

    .principles-grid {
      @apply md:grid-cols-2;
    }
  }

  @media (max-width: 768px) {
    .tiers-grid {
      @apply grid-cols-1;
    }

    .credits-grid {
      @apply grid-cols-1;
    }

    .principles-grid {
      @apply grid-cols-1;
    }

    .faq-grid {
      @apply grid-cols-1;
    }

    h1 {
      @apply text-3xl;
    }

    .cta-content {
      @apply px-6 py-12;
    }
  }
</style>
